/**
 * ЭТАП 2 — начальные параметры каталожного запроса, ОБЩИЕ для
 * useCatalogProducts (клиент) и SSR-префетча (src/ssrPrefetch.ts).
 *
 * Ключ react-query обязан совпасть байт-в-байт между серверным префетчем и
 * ПЕРВЫМ клиентским рендером (сторы ещё не загружены — pristine-значения):
 * isExpress=false (isCurrentAddressValidForExpress — boolean-геттер, false до
 * загрузки корзины), xStoreId=null, addressId=null,
 * epp=EPP_CATALOG_DEFAULT и catalog_sorting='popular' (defaultSettings
 * UserStore). Когда сторы загрузятся и значения изменятся — ключ сменится и
 * react-query перезапросит сам (keepPreviousData удержит SSR-выдачу).
 */
import { getURLProps } from '@utils/setPagesUrl';
import type { CatalogProductsParamsT } from '@queries/index';

/** = EPP_CATALOG_DEFAULT / defaultSettings из UserStore (не экспортируются) */
export const CATALOG_EPP_DEFAULT = 20;
export const CATALOG_SORTING_DEFAULT = 'popular';

/**
 * Сортировка каталога из URL и слага — извлечено 1:1 из setOrderValue
 * (useCatalogProducts), единственный источник правды.
 */
export const resolveCatalogSortValue = (
  pathname: string,
  id: string,
  // после свежего dev настройка приходит из схемы как Maybe<string>
  catalogSorting: string | null | undefined
): string => {
  const { sort, by } = getURLProps({ pathname });

  let urlParseParam = '';

  if (by === 'discount' && sort === 'desc') urlParseParam = 'discount';
  if (by === 'newest' && sort === 'desc') urlParseParam = 'newest';
  if (by === 'price' && sort === 'asc') urlParseParam = 'priceUp';
  if (by === 'price' && sort === 'desc') urlParseParam = 'priceDown';
  if (by === 'title' && sort === 'asc') urlParseParam = 'alphabet';
  if (by === 'alphabet' && sort === 'desc') urlParseParam = 'alphabet';
  if (by === 'date' && sort === 'desc') urlParseParam = 'newest';
  if (by === 'rating' && sort === 'desc') urlParseParam = 'popular';
  if (by === 'comments' && sort === 'desc') urlParseParam = 'comments';
  if (by === 'express' && sort === 'desc') urlParseParam = 'express';

  if (urlParseParam) {
    return urlParseParam;
  }

  if (id === 'newbee') {
    return 'newest';
  }
  if (id === 'rasprodazha') {
    return 'default';
  }
  if (id === 'promo') {
    return catalogSorting || 'discount';
  }
  if (id === 'rating') {
    return 'popular';
  }
  if (id === 'comments') {
    return 'comments';
  }
  return catalogSorting || 'popular';
};

export interface InitialCatalogQueryArgs {
  params: CatalogProductsParamsT;
  initialPage: number;
}

/**
 * Параметры каталожного infinite-запроса для SSR по (pathname, search) —
 * зеркало queryParams из useCatalogProducts при pristine-сторах.
 * null — если это не /catalog/:id.
 */
export const buildInitialCatalogQueryArgs = (
  pathname: string,
  search: string
): InitialCatalogQueryArgs | null => {
  const m = /^\/catalog\/([^/?#]+)/.exec(pathname);
  if (!m) {
    return null;
  }
  const id = decodeURIComponent(m[1]);

  const { page, filter, group, type: typeUrlID } = getURLProps({ pathname });
  const groupsParam = new URLSearchParams(search).get('groups') ?? '';

  return {
    params: {
      slug: id,
      typeUrlID,
      group,
      groups: groupsParam,
      specOffer: filter,
      setOrder: resolveCatalogSortValue(pathname, id, CATALOG_SORTING_DEFAULT),
      isExpress: false,
      epp: CATALOG_EPP_DEFAULT,
      xStoreId: null,
      // ПРОВЕРЕНО по [catalog-hook:key]: pristine cart даёт addressId === null
      // (НЕ undefined — null попадает в JSON-хэш ключа, undefined выпадает)
      addressId: null
    } as CatalogProductsParamsT,
    initialPage: Number(page) || 1
  };
};
